// src/components/admin/payments/PaymentManagement.jsx
import { useState } from 'react';
import {
  FaPaperPlane,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaInbox,
  FaTrash,
  FaEdit,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import useAdminInvoices from '../../../hooks/useAdminInvoices';
import SendInvoiceModal from './SendInvoiceModal';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const fmtDate = (date) => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(date instanceof Date ? date : new Date(date));
};

const STATUS_CONFIG = {
  open: { label: 'Awaiting Payment', icon: FaClock, classes: 'bg-yellow-100 text-yellow-800' },
  paid: { label: 'Paid', icon: FaCheckCircle, classes: 'bg-green-100 text-green-800' },
  payment_failed: { label: 'Payment Failed', icon: FaExclamationTriangle, classes: 'bg-red-100 text-red-800' },
  void: { label: 'Void', icon: FaTimesCircle, classes: 'bg-gray-100 text-gray-500' },
  uncollectible: { label: 'Uncollectible', icon: FaTimesCircle, classes: 'bg-gray-100 text-gray-500' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge = ({ stripeInvoiceStatus }) => {
  const config = STATUS_CONFIG[stripeInvoiceStatus || 'open'] || STATUS_CONFIG.open;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.classes}`}>
      <Icon className="w-3 h-3" /> {config.label}
    </span>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color || 'text-gray-900'}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ── Draft row ─────────────────────────────────────────────────────────────────

const DraftRow = ({ draft, selected, onSelect, onEdit, onApprove, onDelete, sending }) => (
  <div className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors ${selected ? 'bg-blue-50' : ''}`}>
    <input
      type="checkbox"
      checked={selected}
      onChange={() => onSelect(draft.id)}
      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{draft.customerName || '—'}</p>
      <p className="text-xs text-gray-400 truncate">{draft.email}</p>
    </div>
    <div className="text-sm font-semibold text-gray-900 flex-shrink-0">
      {fmt(draft.totalAmount)}
    </div>
    <div className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
      {fmtDate(draft.createdAt)}
    </div>
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 flex-shrink-0">
      Draft
    </span>
    <div className="flex items-center gap-1 flex-shrink-0">
      {/* Edit */}
      <button
        onClick={() => onEdit(draft)}
        disabled={sending}
        title="Edit draft"
        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
      >
        <FaEdit className="w-3.5 h-3.5" />
      </button>
      {/* Send */}
      <button
        onClick={() => onApprove(draft)}
        disabled={sending}
        title="Send this invoice"
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
      >
        <FaPaperPlane className="w-3 h-3" />
        <span className="hidden sm:inline">Send</span>
      </button>
      {/* Delete */}
      <button
        onClick={() => onDelete(draft.id)}
        disabled={sending}
        title="Delete draft"
        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
      >
        <FaTrash className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

// ── Main ──────────────────────────────────────────────────────────────────────

const PaymentManagement = () => {
  const {
    invoices,
    drafts,
    loading,
    draftsLoading,
    sending,
    error,
    clearError,
    sendInvoice,
    saveDraft,
    updateDraft,
    approveDrafts,
    deleteDraft,
    getStats,
  } = useAdminInvoices();

  const [showModal, setShowModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [draftsExpanded, setDraftsExpanded] = useState(true);
  const [selectedDraftIds, setSelectedDraftIds] = useState([]);

  const stats = getStats();

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 6000);
  };

  // ── Modal open/close ──────────────────────────────────────────────────────

  const openNewInvoice = () => {
    setEditingDraft(null);
    setShowModal(true);
  };

  const openEditDraft = (draft) => {
    setEditingDraft(draft);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDraft(null);
  };

  // ── Invoice actions ───────────────────────────────────────────────────────

  const handleSendInvoice = async (invoiceData) => {
    // If coming from edit mode, delete the draft first then send
    if (invoiceData._fromDraftId) {
      await sendInvoice(invoiceData);
      // Draft was already deleted in the hook's approveDrafts, but here we
      // sent directly so delete manually
      await deleteDraft(invoiceData._fromDraftId);
    } else {
      await sendInvoice(invoiceData);
    }
    showSuccess(`Invoice sent to ${invoiceData.customerEmail}`);
  };

  const handleSaveDraft = async (invoiceData) => {
    await saveDraft(invoiceData);
    showSuccess(`Draft saved for ${invoiceData.customerName}`);
  };

  const handleUpdateDraft = async (draftId, invoiceData) => {
    await updateDraft(draftId, invoiceData);
    showSuccess(`Draft updated for ${invoiceData.customerName}`);
  };

  // ── Draft actions ─────────────────────────────────────────────────────────

  const handleSelectDraft = (id) =>
    setSelectedDraftIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );

  const handleSelectAll = () =>
    setSelectedDraftIds(
      selectedDraftIds.length === drafts.length ? [] : drafts.map((d) => d.id)
    );

  const handleApproveOne = async (draft) => {
    try {
      await approveDrafts(draft);
      setSelectedDraftIds((prev) => prev.filter((id) => id !== draft.id));
      showSuccess(`Invoice sent to ${draft.email}`);
    } catch (err) {
      alert(`Failed to send: ${err.message}`);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedDraftIds.length === 0) return;
    const toApprove = drafts.filter((d) => selectedDraftIds.includes(d.id));
    try {
      await approveDrafts(toApprove);
      showSuccess(
        `${toApprove.length} invoice${toApprove.length > 1 ? 's' : ''} sent successfully`
      );
      setSelectedDraftIds([]);
    } catch (err) {
      alert(`Failed to send: ${err.message}`);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await deleteDraft(draftId);
      setSelectedDraftIds((prev) => prev.filter((id) => id !== draftId));
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  // ── Filter ────────────────────────────────────────────────────────────────

  const filteredInvoices =
    filter === 'all'
      ? invoices
      : filter === 'open'
      ? invoices.filter((i) => !i.stripeInvoiceStatus || i.stripeInvoiceStatus === 'open')
      : invoices.filter((i) => i.stripeInvoiceStatus === filter);

  const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Awaiting' },
    { key: 'paid', label: 'Paid' },
    { key: 'payment_failed', label: 'Failed' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-0.5">Send invoices and track payment status</p>
        </div>
        <button
          onClick={openNewInvoice}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
        >
          <FaEdit className="w-4 h-4" /> New Invoice
        </button>
      </div>

      {/* Success */}
      {successMessage && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
          <FaCheckCircle className="w-4 h-4 flex-shrink-0" /> {successMessage}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm">
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
          <button onClick={clearError} className="ml-4 text-red-400 hover:text-red-600">
            <FaTimesCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Revenue" value={fmt(stats.totalRevenue)} sub={`${stats.paid} paid`} color="text-green-700" />
        <StatCard label="Outstanding" value={fmt(stats.totalOutstanding)} sub={`${stats.sent} awaiting`} color="text-yellow-600" />
        <StatCard label="Total Sent" value={stats.total} sub="all time" />
        <StatCard
          label="Drafts"
          value={draftsLoading ? '...' : drafts.length}
          sub="pending approval"
          color={drafts.length > 0 ? 'text-blue-600' : 'text-gray-900'}
        />
      </div>

      {/* ── Drafts section ── */}
      {(draftsLoading || drafts.length > 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
            <button
              onClick={() => setDraftsExpanded((prev) => !prev)}
              className="flex items-center gap-3"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-blue-900">Drafts</h2>
                {drafts.length > 0 && (
                  <span className="text-xs font-bold text-white bg-blue-500 rounded-full px-2 py-0.5">
                    {drafts.length}
                  </span>
                )}
              </div>
              {draftsExpanded
                ? <FaChevronUp className="w-3 h-3 text-blue-400" />
                : <FaChevronDown className="w-3 h-3 text-blue-400" />}
            </button>

            {draftsExpanded && selectedDraftIds.length > 0 && (
              <button
                onClick={handleApproveSelected}
                disabled={sending}
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <FaPaperPlane className="w-3 h-3" />
                Send {selectedDraftIds.length} Selected
              </button>
            )}
          </div>

          {draftsExpanded && (
            <>
              {draftsLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                </div>
              ) : drafts.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">No drafts</div>
              ) : (
                <>
                  <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={selectedDraftIds.length === drafts.length && drafts.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-xs text-gray-500 font-medium">
                      {selectedDraftIds.length === 0
                        ? 'Select all'
                        : `${selectedDraftIds.length} of ${drafts.length} selected`}
                    </span>
                  </div>
                  {drafts.map((draft) => (
                    <DraftRow
                      key={draft.id}
                      draft={draft}
                      selected={selectedDraftIds.includes(draft.id)}
                      onSelect={handleSelectDraft}
                      onEdit={openEditDraft}
                      onApprove={handleApproveOne}
                      onDelete={handleDeleteDraft}
                      sending={sending}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Sent invoices ── */}
      <div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-2">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-green-500 rounded-full animate-spin mx-auto" />
                <p className="text-sm text-gray-400">Loading invoices...</p>
              </div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <FaInbox className="w-10 h-10 mb-3 text-gray-300" />
              <p className="text-sm font-medium text-gray-500">
                {filter === 'all' ? 'No invoices sent yet' : `No ${filter} invoices`}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      {['Customer', 'Amount', 'Status', 'Sent', 'Due / Paid', ''].map((h, i) => (
                        <th key={i} className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{invoice.customerName || '—'}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{invoice.email}</p>
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-900">{fmt(invoice.totalAmount)}</td>
                        <td className="px-6 py-4"><StatusBadge stripeInvoiceStatus={invoice.stripeInvoiceStatus} /></td>
                        <td className="px-6 py-4 text-gray-500">{fmtDate(invoice.sentAt)}</td>
                        <td className="px-6 py-4 text-gray-500">
                          {invoice.stripeInvoiceStatus === 'paid'
                            ? fmtDate(invoice.paidAt)
                            : fmtDate(invoice.dueDate)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {invoice.hostedInvoiceUrl && (
                            <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700">
                              View in Stripe <FaExternalLinkAlt className="w-3 h-3" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {filteredInvoices.map((invoice) => (
                  <div key={invoice.id} className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.customerName || '—'}</p>
                        <p className="text-xs text-gray-400">{invoice.email}</p>
                      </div>
                      <span className="font-bold text-gray-900">{fmt(invoice.totalAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <StatusBadge stripeInvoiceStatus={invoice.stripeInvoiceStatus} />
                      <span className="text-xs text-gray-400">{fmtDate(invoice.sentAt)}</span>
                    </div>
                    {invoice.hostedInvoiceUrl && (
                      <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600">
                        View in Stripe <FaExternalLinkAlt className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <SendInvoiceModal
        isOpen={showModal}
        onClose={closeModal}
        onInvoiceSent={handleSendInvoice}
        onSaveDraft={handleSaveDraft}
        onUpdateDraft={handleUpdateDraft}
        sending={sending}
        editingDraft={editingDraft}
      />
    </div>
  );
};

export default PaymentManagement;